import { SortDirection } from "@enums/SortDirection";
import { SortType } from "@enums/SortType";
import { IComboBox } from "@interfaces/IComboBox";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import * as moment from "moment";
import { IVeriPillNavLabel } from "../veriSimpleNav/veriSimpleNav.component";
import "./veriList.scss";

interface IVeriListBindings {
    showSearch: boolean;
    searchPlaceholder: string;

    onRowClicked: (args: { row: IVeriListRow<any> }) => void;
    onRowHover: (args: { row: IVeriListRow<any> }) => void;
    onRowsUpdated: (args: { rows: IVeriListRow<any>[] }) => void;

    selectedItem: any;
    templateUrl: string;
    filterOptions: IComboBox[];
    selectedFilterOption: number;
    rowClassName: string;
    itemLabel: string;

    showOptionsAsNav: boolean;
    clickable: boolean;
    loading: boolean;
}

export interface IVeriListRow<T> {
    id?: number;
    data?: T;
    searchString?: string;
    sortData?: any;
    contents?: string;
    filterData?: number[];
    className?: string;
}

//interface IVeriListComboBox extends IComboBox {
//    count?: number;
//}

class VeriListComponentController implements angular.IComponentController, IVeriListBindings {

    sortDirection: SortDirection;
    sortType: SortType;
    showSearch: boolean;
    clickable: boolean;

    loading: boolean;

    rows: IVeriListRow<any>[] = [];
    displayRows: IVeriListRow<any>[] = [];

    searchString: string;
    searchPlaceholder: string;

    customSorter: (args: { row1: IVeriListRow<any>, row2: IVeriListRow<any> }) => number;

    onRowClicked: (args: { row: IVeriListRow<any> }) => void;
    onRowHover: (args: { row: IVeriListRow<any>, mouseEvent: MouseEvent }) => void;
    onRowsUpdated: (args: { rows: IVeriListRow<any>[] }) => void;

    rowClassName: string;
    templateUrl: string;
    itemLabel = this.translationService.t('veriList.items');

    showOptionsAsNav: boolean;
    filterOptions: IComboBox[];
    navLabels: IVeriPillNavLabel[];

    selectedFilterOption: number;

    selectedItem: any;
    selectedRowId: number;
    selectedRowIndex: number;

    resourceCount: number;

    static $inject = ['translationService','$scope'];

    constructor(
        private readonly translationService: TranslationService,
        private readonly $scope: angular.IScope
    ) {

    }

    $onInit(): void {
        this.itemLabel = this.itemLabel;
        this.sortType = this.sortType || SortType.String;
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {

        var sortRows = false;
        var searchRows = false;

        if (onChangesObj["rows"] && this.rows) {

            this.cleanRows();

            sortRows = true;
            searchRows = true;
        }

        if (onChangesObj["selectedRowId"] && this.displayRows) {
            if (this.selectedItem &&
                this.selectedItem.Id &&
                this.selectedRowId &&
                this.selectedItem.Id === this.selectedRowId) {
                return;
            }

            //console.log("Selected", this.selectedRowId);
            this.displayRows.forEach((row, index) => {
                if (this.selectedRowId === row.id) {

                    this.selectedRowIndex = index - 1;
                    //console.log("Index: ", this.selectedRowIndex, row.id, this.selectedRowId);
                }
            });
        }

        if (onChangesObj["selectedFilterOption"]) {
            this.onFilterOptionChanged();
        }

        if (sortRows) {
            //console.log("Sorting rows");
            this.sortRows();
        }

        if (searchRows) {
            this.searchRows();
        }

        if (this.filterOptions) {
            this.calculateNumFilterOptions();
        }
        this.resourceCount = this.displayRows.length;
    }

    rowClicked(row: IVeriListRow<any>) {

        if (this.onRowClicked) {
            this.onRowClicked({ row: row });
        }

        if (this.selectedItem && this.selectedItem.data === row.data) {
            this.selectedItem = null;
        }
        else {
            this.selectedItem = row.data;
        }
    }

    rowMouseEnter(row: IVeriListRow<any>, mouseEvent: MouseEvent) {
        if (this.onRowHover) {
            this.onRowHover({ row: row, mouseEvent: mouseEvent });
        }
    }

    mouseLeave() {
        if (this.onRowHover) {
            this.onRowHover({ row: null, mouseEvent: null });
        }
    }

    onFilterOptionChanged() {

        this.selectedFilterOption = Number(this.selectedFilterOption)
        this.searchRows();
    }

    onNavLabelClicked($label: IVeriPillNavLabel) {
        this.selectedFilterOption = this.navLabels.indexOf($label);
        this.onFilterOptionChanged();
    }

    onSearch() {
        this.$scope.$applyAsync(() => this.searchRows());
    }

    // ----------------------------------------------

    private cleanRows() {

        this.rows.forEach(row => {
            row.searchString = row.searchString || row.contents || "";

            if (!row.sortData) {
                row.sortData = row.contents;
            }

            if (this.sortType == SortType.String) {
                row.sortData = row.sortData.toLocaleLowerCase();
            }

            row.searchString += row.sortData;
            row.searchString = row.searchString.toLocaleLowerCase();
        });
    }

    private searchRows() {

        const safeSearchString = this.searchString ? this.searchString.trim().toLocaleLowerCase() : null;

        if ((safeSearchString || this.selectedFilterOption) && this.rows) {
            this.displayRows = [];

            for (let ri = 0; ri < this.rows.length; ri++) {
                const row = this.rows[ri];
                let rowMatched = false;

                if (safeSearchString && row.searchString.indexOf(safeSearchString) >= 0) {
                    rowMatched = true;
                }
                else if (!safeSearchString) {
                    rowMatched = true;
                }

                if (this.selectedFilterOption && this.selectedFilterOption != -1 && rowMatched) {
                    rowMatched = row.filterData && row.filterData.indexOf(this.selectedFilterOption) >= 0;
                }

                if (rowMatched) {
                    this.displayRows.push(row);
                }
            }
        }
        else {
            this.displayRows = this.rows;
        }

        if (this.onRowsUpdated) {
            this.onRowsUpdated({ rows: this.displayRows });
        }
    }

    private sortRows() {
        if (!this.rows) {
            return;
        }

        this.sortType = this.sortType || SortType.String;

        if (this.sortDirection === undefined) {
            this.sortDirection = SortDirection.Ascending;
        }

        this.rows.sort((row1, row2) => {

            if (!row1.sortData || !row2.sortData) {
                return;
            }

            if (this.customSorter && this.customSorter({ row1: row1, row2: row2 })) {
                var response = this.customSorter({ row1: row1, row2: row2 });

                // If both are the same, then allow us to continue to filter
                if (response != 0) { return response; }
            }

            if (this.sortType === SortType.Date) {
                return this.sortDirection === SortDirection.Ascending
                    ? moment(row1.sortData).diff(row2.sortData, "s")
                    : moment(row2.sortData).diff(row1.sortData, "s");
            }
            else if (this.sortType === SortType.Number) {
                return this.sortDirection === SortDirection.Ascending
                    ? row1.sortData < row2.sortData ? 1 : -1
                    : row1.sortData < row2.sortData ? -1 : 1;
            }
            else {
                return this.sortDirection === SortDirection.Ascending
                    ? row1.sortData.localeCompare(row2.sortData)
                    : row2.sortData.localeCompare(row1.sortData);
            }
        });
    }

    private calculateNumFilterOptions() {
        if (!this.rows) {
            return;
        }

        var hasAllOption = false;
        this.filterOptions.forEach(filterOption => {
            if (filterOption.Value <= 0) {
                hasAllOption = true;
            }
        });

        if (!hasAllOption) {
            this.filterOptions.unshift({
                Name: this.translationService.t("veriList.all"),
                Value: 0
            });
        }

        if (!this.selectedFilterOption) {
            this.selectedFilterOption = this.filterOptions[0].Value;
        }

        this.filterOptions.forEach(filterOption => {
            filterOption.Count = 0;
        });

        for (var i = 0; i < this.rows.length; i++) {
            const row = this.rows[i];

            if (!row.filterData) {
                continue;
            }

            for (var j = 0; j < this.filterOptions.length; j++) {
                const option = this.filterOptions[j];

                if (option.Value <= 0 || row.filterData.indexOf(option.Value) >= 0) {
                    option.Count++;
                }
            }
        }

        this.updateNavPillLabels();
    }

    private updateNavPillLabels() {
        this.navLabels = [];

        this.filterOptions.forEach(option => {
            this.navLabels.push({
                count: option.Count,
                label: option.Name
            });
        });
    }
}

export class VeriListComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            rows: "<",

            selectedItem: "<",
            sortType: "<?",
            sortDirection: "<",
            filterOptions: "<",
            selectedFilterOption: "<",
            selectedRowId: "<",
            showOptionsAsNav: "<",

            showSearch: "<",

            clickable: "<",
            loading: "<",

            searchPlaceholder: "@",
            filterPlaceholder: "@",
            templateUrl: "@",
            rowClassName: "@",
            itemLabel: "@",

            customSorter: "&",

            onRowsUpdated: "&",
            onRowClicked: "&",
            onRowHover: "&"
        };

        this.controller = VeriListComponentController;
        this.template = require("./veriList.html");
    }
}
